import {
  ProductCategoryEnum,
  ProductKindEnum,
  ProductTypesEnum,
} from '@iheartjane/types';
import { z } from 'zod';

/**
 * Local copies of small schemas previously imported from `@jane/shared/types`.
 * Kept local so `@jane/shared/types` can be pure TypeScript (no Zod).
 * These are used to compose `zMenuProductForProductsTable` and
 * `z_GlobalSpecialProduct`, which validate API responses at runtime.
 */

const zId = z.coerce.number();

const zPriceId = z.union([
  z.enum([
    'eighth_ounce',
    'gram',
    'half_gram',
    'half_ounce',
    'ounce',
    'quarter_ounce',
    'two_gram',
    'each',
  ]),
  z.literal('each'),
]);

export const zPhoto = z.object({
  id: zId,
  position: z.number(),
  urls: z.object({
    extraLarge: z.string(),
    medium: z.string(),
    original: z.string(),
    small: z.string(),
  }),
});

// Mirror of `zAbbreviatedMenuProduct` from shared/types.
// See api/app/serializers/abbreviated_menu_product_serializer.rb
export const zAbbreviatedMenuProduct = z.object({
  id: zId,
  brand_subtype: z.string().nullable(),
  brand: z.string().nullable(),
  kind: z.enum(ProductKindEnum),
  name: z.string(),
  price_each: z.string().nullable(),
  price_eighth_ounce: z.string().nullable(),
  price_gram: z.string().nullable(),
  price_half_gram: z.string().nullable(),
  price_half_ounce: z.string().nullable(),
  price_id: zPriceId.nullish(),
  price_ounce: z.string().nullable(),
  price_quarter_ounce: z.string().nullable(),
  price_two_gram: z.string().nullable(),
  product_brand_id: z.number().nullish(),
  product_id: z.number(),
  type: z.enum(ProductTypesEnum),
});

// Only the `zMenuProduct` fields actually consumed by business-admin
// compositions are exposed here. Previously these were accessed via
// `zMenuProduct.pick(...)` / `zMenuProduct.shape.*`.
export const zMenuProductCategory = z.enum(ProductCategoryEnum).nullable();

export const zMenuProductSubset = z.object({
  amount: z.string().nullable(),
  category: zMenuProductCategory,
  description: z.string(),
  dosage: z.string().nullable(),
  effects: z.array(z.object({ id: zId, title: z.string(), value: z.string() })),
  flavors: z.array(z.object({ id: zId, title: z.string(), value: z.string() })),
  image_urls: z.array(z.string()),
  percent_thc: z.number().nullable(),
  percent_cbd: z.number().nullable(),
  photos: z.array(zPhoto),
});
